import React, { useState, useRef, useEffect } from 'react';
import { sendBulkMessage } from '../../services/api';
import { getSocket } from '../../services/socket';

const AUDIENCE_OPTIONS = [
  { id: 'all',    icon: '🌍', label: 'All Users',         desc: 'Send to every registered user',  color: '#FFB300' },
  { id: 'active', icon: '✅', label: 'Active Users Only', desc: 'Users who are not blocked',       color: '#00aaff' },
  { id: 'vip',    icon: '⭐', label: 'VIP Members Only',  desc: 'Exclusive VIP players only',      color: '#ffd700' },
];

export default function BulkMessagingPage() {
  const [audience,     setAudience]    = useState('all');
  const [title,        setTitle]       = useState('');
  const [content,      setContent]     = useState('');
  const [file,         setFile]        = useState(null);
  const [filePreview,  setFilePreview] = useState(null);
  const [submitting,   setSubmitting]  = useState(false);
  const [result,       setResult]      = useState(null);
  const [showConfirm,  setShowConfirm] = useState(false);
  const [job,          setJob]         = useState(null);

  const fileRef = useRef();

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onProgress = (data) => {
      setJob(data);
      if (data.done) {
        setSubmitting(false);
        setResult({ success: true, count: data.sent, failed: data.failed });
      }
    };
    socket.on('bulk:progress', onProgress);
    return () => socket.off('bulk:progress', onProgress);
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setFilePreview(f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
  };

  const handleSend = () => {
    if (!title.trim() || !content.trim()) return alert('Please fill in title and message content');
    setShowConfirm(true);
  };

  const handleConfirmed = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    setResult(null);
    setJob(null);
    try {
      const fd = new FormData();
      fd.append('title',    title.trim());
      fd.append('content',  content.trim());
      fd.append('audience', audience);
      if (file) fd.append('file', file);
      const { data } = await sendBulkMessage(fd);
      setJob({ jobId: data.jobId, sent: 0, failed: 0, total: data.total, progress: 0, done: false });
      setTitle('');
      setContent('');
      setFile(null);
      setFilePreview(null);
    } catch (err) {
      setSubmitting(false);
      setResult({ success: false, error: err.response?.data?.message || 'Failed to send' });
    }
  };

  const selectedAudience = AUDIENCE_OPTIONS.find(a => a.id === audience);

  return (
    <div style={styles.container}>

      {/* ── Custom Confirm Modal ─────────────────────────────────────────── */}
      {showConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalIcon}>📣</div>
            <div style={styles.modalTitle}>Confirm Broadcast</div>
            <div style={styles.modalDesc}>
              You are about to send this message to{' '}
              <span style={{ color: selectedAudience?.color, fontWeight: '700' }}>
                {selectedAudience?.icon} {selectedAudience?.label}
              </span>
            </div>
            <div style={styles.modalPreview}>
              <div style={styles.modalPreviewTitle}>📣 {title}</div>
              <div style={styles.modalPreviewContent}>{content}</div>
              {filePreview && (
                <img src={filePreview} alt="preview" style={styles.modalPreviewImg} />
              )}
            </div>
            <div style={styles.modalActions}>
              <button style={styles.modalCancel} onClick={() => setShowConfirm(false)}>
                Cancel
              </button>
              <button
                style={{ ...styles.modalConfirm, background: `linear-gradient(90deg, ${selectedAudience?.color}, #00aaff)` }}
                onClick={handleConfirmed}
              >
                Yes, Send Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Bulk Messaging</h1>
          <p style={styles.pageSubtitle}>Broadcast messages to your players</p>
        </div>
      </div>

      <div style={styles.layout}>
        {/* ── Left: Form ─────────────────────────────────────────────────── */}
        <div style={styles.formCard}>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>SELECT AUDIENCE</div>
            <div style={styles.audienceGrid}>
              {AUDIENCE_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setAudience(opt.id)}
                  style={{
                    ...styles.audienceBtn,
                    ...(audience === opt.id ? { ...styles.audienceBtnActive, borderColor: opt.color, boxShadow: `0 0 16px ${opt.color}22` } : {}),
                  }}
                >
                  <span style={styles.audienceIcon}>{opt.icon}</span>
                  <span style={{ ...styles.audienceLabel, color: audience === opt.id ? opt.color : '#e2e8f0' }}>{opt.label}</span>
                  <span style={styles.audienceDesc}>{opt.desc}</span>
                  {audience === opt.id && <span style={{ ...styles.audienceCheck, color: opt.color }}>✓</span>}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>MESSAGE TITLE *</div>
            <input
              style={styles.input}
              placeholder="e.g. 🎉 Special Weekend Bonus!"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={100}
            />
            <div style={styles.charCount}>{title.length}/100</div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>MESSAGE CONTENT *</div>
            <textarea
              style={{ ...styles.input, height: '130px', resize: 'vertical' }}
              placeholder="Write your message here..."
              value={content}
              onChange={e => setContent(e.target.value)}
              maxLength={1000}
            />
            <div style={styles.charCount}>{content.length}/1000</div>
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>ATTACH IMAGE (Optional)</div>
            <div style={styles.fileRow}>
              <button style={styles.fileBtn} onClick={() => fileRef.current?.click()}>
                📎 {file ? file.name : 'Choose File'}
              </button>
              {file && (
                <button style={styles.clearFileBtn} onClick={() => { setFile(null); setFilePreview(null); }}>✕</button>
              )}
            </div>
            <div style={styles.fileHint}>PNG, JPG, JPEG accepted</div>
            <input ref={fileRef} type="file" accept="image/png,image/jpg,image/jpeg" style={{ display: 'none' }} onChange={handleFileChange} />
            {filePreview && <img src={filePreview} alt="preview" style={styles.filePreview} />}
          </div>

          {job && (
            <div style={styles.progressBox}>
              <div style={styles.progressHeader}>
                <span style={styles.progressLabel}>
                  {job.done ? '✅ Broadcast Complete' : '📡 Sending broadcast...'}
                </span>
                <span style={styles.progressCount}>{job.sent} / {job.total}</span>
              </div>
              <div style={styles.progressTrack}>
                <div style={{
                  ...styles.progressFill,
                  width: `${job.progress}%`,
                  background: job.done ? 'linear-gradient(90deg,#00ff88,#FFB300)' : 'linear-gradient(90deg,#FFB300,#00aaff)',
                }} />
              </div>
              <div style={styles.progressStats}>
                <span style={{ color: '#00ff88' }}>✓ {job.sent} sent</span>
                {job.failed > 0 && <span style={{ color: '#ff6b6b' }}>✗ {job.failed} failed</span>}
                <span style={{ color: 'rgba(255,255,255,.25)' }}>{job.progress}%</span>
              </div>
            </div>
          )}

          {result && (
            <div style={{ ...styles.resultBox, ...(result.success ? styles.resultSuccess : styles.resultError) }}>
              {result.success
                ? `✅ Sent to ${result.count} users!${result.failed ? ` (${result.failed} failed)` : ''}`
                : `❌ ${result.error}`}
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={submitting || !title.trim() || !content.trim()}
            style={{ ...styles.sendBtn, ...(!title.trim() || !content.trim() || submitting ? styles.sendBtnDisabled : {}) }}
          >
            {submitting ? '📡 Sending...' : `📣 Send to ${selectedAudience?.label}`}
          </button>
        </div>

        {/* ── Right: Preview ──────────────────────────────────────────────── */}
        <div style={styles.previewCol}>
          <div style={styles.previewLabel}>MESSAGE PREVIEW</div>
          <div style={styles.phoneFrame}>
            <div style={styles.phoneScreen}>
              <div style={styles.notifCard}>
                <div style={styles.notifHeader}>
                  <img src="/logo.jpg" alt="" style={{ height: "16px", width: "auto", objectFit: "contain", verticalAlign: "middle" }} />
                  <span style={styles.notifApp}>AIA74</span>
                  <span style={styles.notifTime}>now</span>
                </div>
                <div style={styles.notifTitle}>{title || 'Your message title...'}</div>
                <div style={styles.notifContent}>{content || 'Your message content will appear here...'}</div>
                {filePreview && <img src={filePreview} alt="" style={styles.notifImg} />}
              </div>

              <div style={styles.bulkCardPreview}>
                <div style={styles.bulkPreviewAccent} />
                {filePreview && <img src={filePreview} alt="" style={styles.bulkPreviewImg} />}
                <div style={styles.bulkPreviewBody}>
                  <div style={styles.bulkPreviewTitleRow}>
                    <span>📣</span>
                    <span style={styles.bulkPreviewTitle}>{title || 'Announcement Title'}</span>
                  </div>
                  <div style={styles.bulkPreviewDivider} />
                  <div style={styles.bulkPreviewContent}>{content || 'Your message description...'}</div>
                  <div style={styles.bulkPreviewFooter}>
                    <span style={{ fontSize: '9px', color: 'rgba(255,255,255,.25)' }}>now</span>
                    <span style={{ fontSize: '9px', color: 'rgba(255,179,0,.5)', fontWeight: '700', letterSpacing: '1px' }}>OFFICIAL ANNOUNCEMENT</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.audienceSummary}>
            <div style={styles.summaryTitle}>SENDING TO</div>
            <div style={{ ...styles.summaryValue, color: selectedAudience?.color }}>
              {selectedAudience?.icon} {selectedAudience?.label}
            </div>
            <div style={styles.summaryDesc}>{selectedAudience?.desc}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container:       { flex: 1, padding: '28px', overflowY: 'auto', fontFamily: "'Exo 2', sans-serif", background: '#080c14' },
  pageHeader:      { marginBottom: '24px' },
  pageTitle:       { margin: 0, fontSize: '24px', fontWeight: '700', color: '#e2e8f0', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '1px' },
  pageSubtitle:    { margin: '4px 0 0', color: 'rgba(255,255,255,0.3)', fontSize: '13px' },
  layout:          { display: 'flex', gap: '24px', flexWrap: 'wrap' },
  formCard:        { flex: 1, minWidth: '320px', background: '#0d1420', borderRadius: '16px', border: '1px solid rgba(255,179,0,0.08)', padding: '28px' },
  section:         { marginBottom: '22px' },
  sectionTitle:    { fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,179,0,0.6)', marginBottom: '12px' },
  audienceGrid:    { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  audienceBtn:     { flex: 1, minWidth: '120px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '14px 12px', cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative', transition: 'all 0.15s' },
  audienceBtnActive: { background: 'rgba(255,179,0,0.05)' },
  audienceIcon:    { fontSize: '22px' },
  audienceLabel:   { fontSize: '13px', fontWeight: '700', fontFamily: "'Exo 2', sans-serif" },
  audienceDesc:    { fontSize: '11px', color: 'rgba(255,255,255,0.25)' },
  audienceCheck:   { position: 'absolute', top: '10px', right: '12px', fontSize: '16px', fontWeight: '900' },
  input:           { width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,179,0,0.15)', borderRadius: '10px', padding: '12px 14px', color: '#fff', fontSize: '14px', fontFamily: "'Exo 2', sans-serif", outline: 'none', boxSizing: 'border-box' },
  charCount:       { textAlign: 'right', fontSize: '11px', color: 'rgba(255,255,255,0.2)', marginTop: '5px' },
  fileRow:         { display: 'flex', gap: '8px', alignItems: 'center' },
  fileBtn:         { background: 'rgba(255,179,0,0.06)', border: '1px solid rgba(255,179,0,0.2)', borderRadius: '10px', color: '#FFB300', padding: '10px 16px', cursor: 'pointer', fontSize: '13px', fontFamily: "'Exo 2', sans-serif", fontWeight: '600', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  clearFileBtn:    { background: 'rgba(255,50,50,0.1)', border: '1px solid rgba(255,50,50,0.2)', borderRadius: '8px', color: '#ff6b6b', width: '34px', height: '34px', cursor: 'pointer', fontSize: '14px' },
  fileHint:        { color: 'rgba(255,255,255,0.2)', fontSize: '11px', marginTop: '6px' },
  filePreview:     { width: '100%', maxHeight: '140px', objectFit: 'cover', borderRadius: '10px', marginTop: '10px', border: '1px solid rgba(255,179,0,0.1)' },
  progressBox:     { background: 'rgba(255,179,0,0.04)', border: '1px solid rgba(255,179,0,0.15)', borderRadius: '12px', padding: '16px', marginBottom: '16px' },
  progressHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  progressLabel:   { color: '#e2e8f0', fontSize: '13px', fontWeight: '600' },
  progressCount:   { color: '#FFB300', fontSize: '13px', fontWeight: '700', fontFamily: "'Rajdhani', sans-serif" },
  progressTrack:   { height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' },
  progressFill:    { height: '100%', borderRadius: '3px', transition: 'width 0.4s ease' },
  progressStats:   { display: 'flex', gap: '16px', fontSize: '11px' },
  resultBox:       { padding: '13px 16px', borderRadius: '10px', fontSize: '14px', marginBottom: '16px' },
  resultSuccess:   { background: 'rgba(0,255,100,0.08)', border: '1px solid rgba(0,255,100,0.2)', color: '#00ff88' },
  resultError:     { background: 'rgba(255,50,50,0.08)', border: '1px solid rgba(255,50,50,0.2)', color: '#ff6b6b' },
  sendBtn:         { width: '100%', background: 'linear-gradient(90deg, #FFB300, #00aaff)', border: 'none', borderRadius: '12px', padding: '15px', color: '#080c14', fontSize: '16px', fontWeight: '900', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '2px', cursor: 'pointer' },
  sendBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  previewCol:          { width: '260px', minWidth: '240px' },
  previewLabel:        { fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,179,0,0.5)', marginBottom: '12px' },
  phoneFrame:          { background: '#0d1420', border: '2px solid rgba(255,179,0,0.15)', borderRadius: '24px', padding: '16px', marginBottom: '16px' },
  phoneScreen:         { display: 'flex', flexDirection: 'column', gap: '12px' },
  notifCard:           { background: 'rgba(255,255,255,0.05)', borderRadius: '14px', padding: '12px', border: '1px solid rgba(255,255,255,0.06)' },
  notifHeader:         { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' },
  notifIcon:           { fontSize: '12px' },
  notifApp:            { fontSize: '10px', color: '#FFB300', fontWeight: '700', letterSpacing: '2px', flex: 1 },
  notifTime:           { fontSize: '10px', color: 'rgba(255,255,255,0.25)' },
  notifTitle:          { fontSize: '13px', fontWeight: '700', color: '#e2e8f0', marginBottom: '3px' },
  notifContent:        { fontSize: '11px', color: 'rgba(255,255,255,0.4)', lineHeight: '1.4' },
  notifImg:            { width: '100%', borderRadius: '8px', marginTop: '8px', maxHeight: '80px', objectFit: 'cover' },
  bulkCardPreview:     { background: 'linear-gradient(145deg,#0d1a2a,#0a1520)', border: '1px solid rgba(255,179,0,0.2)', borderRadius: '14px', overflow: 'hidden' },
  bulkPreviewAccent:   { height: '3px', background: 'linear-gradient(90deg,#FFB300,#00aaff,#7b61ff)' },
  bulkPreviewImg:      { width: '100%', height: '90px', objectFit: 'cover', display: 'block' },
  bulkPreviewBody:     { padding: '10px 12px 8px' },
  bulkPreviewTitleRow: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' },
  bulkPreviewTitle:    { fontSize: '12px', fontWeight: '800', color: '#e2e8f0', fontFamily: "'Rajdhani',sans-serif" },
  bulkPreviewDivider:  { height: '1px', background: 'linear-gradient(90deg,rgba(255,179,0,0.3),transparent)', marginBottom: '6px' },
  bulkPreviewContent:  { fontSize: '10px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.5', marginBottom: '8px' },
  bulkPreviewFooter:   { display: 'flex', justifyContent: 'space-between' },
  audienceSummary:     { background: '#0d1420', borderRadius: '14px', border: '1px solid rgba(255,179,0,0.08)', padding: '16px' },
  summaryTitle:        { fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.2)', marginBottom: '8px' },
  summaryValue:        { fontSize: '16px', fontWeight: '700', fontFamily: "'Rajdhani', sans-serif", marginBottom: '4px' },
  summaryDesc:         { fontSize: '12px', color: 'rgba(255,255,255,0.3)' },

  // Modal
  modalOverlay:        { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal:               { background: '#0d1420', border: '1px solid rgba(255,179,0,0.2)', borderRadius: '20px', padding: '32px 28px', maxWidth: '420px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.7)' },
  modalIcon:           { fontSize: '44px', textAlign: 'center', marginBottom: '14px' },
  modalTitle:          { fontSize: '22px', fontWeight: '700', color: '#e2e8f0', textAlign: 'center', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '1px', marginBottom: '8px' },
  modalDesc:           { fontSize: '14px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginBottom: '20px', lineHeight: '1.6' },
  modalPreview:        { background: 'rgba(255,179,0,0.04)', border: '1px solid rgba(255,179,0,0.12)', borderRadius: '14px', padding: '14px 16px', marginBottom: '24px' },
  modalPreviewTitle:   { color: '#FFB300', fontSize: '14px', fontWeight: '700', marginBottom: '6px' },
  modalPreviewContent: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', lineHeight: '1.5', maxHeight: '60px', overflow: 'hidden' },
  modalPreviewImg:     { width: '100%', maxHeight: '80px', objectFit: 'cover', borderRadius: '8px', marginTop: '10px' },
  modalActions:        { display: 'flex', gap: '12px' },
  modalCancel:         { flex: 1, padding: '13px', borderRadius: '11px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'rgba(255,255,255,0.4)', fontSize: '14px', cursor: 'pointer', fontFamily: "'Exo 2', sans-serif", fontWeight: '600' },
  modalConfirm:        { flex: 1, padding: '13px', borderRadius: '11px', border: 'none', color: '#080c14', fontSize: '15px', fontWeight: '900', cursor: 'pointer', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '1px' },
};
